import { useMemo, useState, useEffect } from "react";
import { kFormatterVerra } from "hooks/utils/formattingUtils";
import "./selected-features-panel.css";
import "./stats-panel-container.css";
import "./custom-number-input.css";

import layerGroups from "layers/layers";
import { LayerGroupName, LayerName } from "types/dataModel";

import { MapGeoJSONFeature } from "maplibre-gl";
import { MultiSwitch, MultiSwitchOption } from "components/multi-switch";
import { Headline, Text } from "components/typography";

export const VERRA_REF_YEAR = 2015;

interface NumberInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  className?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
}

function NumberInput({
  value,
  onChange,
  onKeyDown,
  placeholder,
  className = "",
  min = 0,
  max = Infinity,
  step = 1,
  disabled = false,
}: NumberInputProps) {
  const handleIncrement = () => {
    if (disabled) return;
    const currentValue = Number(value) || 0;
    const newValue = Math.min(max, currentValue + step);
    onChange(newValue.toString());
  };

  const handleDecrement = () => {
    if (disabled) return;
    const currentValue = Number(value) || 0;
    const newValue = Math.max(min, currentValue - step);
    onChange(newValue.toString());
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      handleIncrement();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      handleDecrement();
    }
    onKeyDown?.(e);
  };

  return (
    <div className="custom-number-input">
      <input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={className}
      />
      <div className="spinner-buttons">
        <button
          type="button"
          className="spinner-button"
          onClick={handleIncrement}
          disabled={disabled || (Number(value) || 0) >= max}
          aria-label="Increment"
        />
        <button
          type="button"
          className="spinner-button"
          onClick={handleDecrement}
          disabled={disabled || (Number(value) || 0) <= min}
          aria-label="Decrement"
        />
      </div>
    </div>
  );
}

function MetricContent({
  children,
  contentModifier,
}: {
  children: React.ReactNode;
  contentModifier?: string;
}) {
  return (
    <div
      className={
        "aeb-content-container " + (contentModifier ? contentModifier : "")
      }
    >
      {children}
    </div>
  );
}

function MetricTitle({
  title,
  Icon,
  selected = false,
  clickable = false,
}: {
  title: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  selected?: boolean;
  clickable?: boolean;
}) {
  return (
    <div
      className={
        "aeb-container-title flex flex-row items-center" +
        (selected ? " selected" : "") +
        (clickable ? " clickable" : "")
      }
    >
      <div className="layer-image-container">
        <Icon fill="white" />
      </div>
      <div className="aeb-title-text-container">
        <Headline level={3} className="text-white my-1">
          {title}
        </Headline>
      </div>
    </div>
  );
}

function TemplateMetricContainer({
  title,
  icon,
  children,
  contentModifier,
  description = "Metric specific description to come here. Only shown when section is hovered.",
}: {
  title: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  children: React.ReactNode;
  contentModifier?: string;
  description?: string;
}) {
  return (
    <div className="aeb-container text-left">
      <MetricTitle Icon={icon} title={title} />
      <MetricContent contentModifier={contentModifier}>
        <Text
          level={2}
          className="aeb-container-metric-description px-2 text-neutral-midfi-grey"
        >
          {description}
        </Text>
        {children}
      </MetricContent>
    </div>
  );
}

enum ResilienceCreditType {
  PROPERTY = "PROPERTY",
  PEOPLE = "PEOPLE",
}

function ResilienceCreditCalculator({
  resilienceCreditsPerHectare,
  hectares,
  setHectares,
  type = ResilienceCreditType.PROPERTY,
}: {
  resilienceCreditsPerHectare: number;
  hectares: number;
  setHectares: (hectares: number) => void;
  type: ResilienceCreditType;
}) {
  const [inputValue, setInputValue] = useState<string>(hectares.toString());
  const [isValid, setIsValid] = useState<boolean>(true);

  // Sync input value when hectares prop changes
  useEffect(() => {
    setInputValue(hectares.toString());
  }, [hectares]);

  const totalValue = hectares * resilienceCreditsPerHectare;

  return (
    <div className="w-full flex flex-col items-center pl-2 pr-8">
      <div className="w-full flex justify-between mb-6">
        <div className="w-1/2 pr-1 flex flex-col items-start">
          <Text
            as="label"
            level={2}
            className="text-left flex items-center pb-1 text-neutral-midfi-grey"
          >
            Project Size (Hectares):
          </Text>
          <div className="w-3/4 flex flex-col">
            <NumberInput
              value={inputValue}
              onChange={(value) => {
                setInputValue(value);

                const hasNonNumericChars =
                  value !== "" && !/^[0-9]*\.?[0-9]*$/.test(value);

                const numValue = Number(value);
                const isValidNumber =
                  value === "" || (!isNaN(numValue) && numValue >= 0);

                if (hasNonNumericChars) {
                  setIsValid(false);
                } else if (!isValidNumber) {
                  setIsValid(false);
                } else {
                  setIsValid(true);
                }

                if (isValidNumber && !hasNonNumericChars) {
                  if (value === "" || value === "0") {
                    setHectares(0);
                  } else {
                    setHectares(numValue);
                  }
                }
              }}
              placeholder="1"
              min={0}
              step={1}
              className={`text-lg text-center lining-nums ${
                !isValid ? "border-red-500 bg-red-50" : ""
              }`}
            />
          </div>
        </div>
        <div className="w-1/2 pl-1 flex flex-col items-end">
          <Text
            as="label"
            level={2}
            className="text-right flex items-center pb-1 text-neutral-midfi-grey"
          >
            Benefits per Hectare:
          </Text>
          <div className="w-1/2 flex flex-row items-center p-1">
            {type === ResilienceCreditType.PROPERTY && (
              <span className="mr-1 flex items-center text-lg">$</span>
            )}
            <Headline
              level={4}
              className="text-2xl flex flex-row items-center text-neutral-midfi-grey"
            >
              {kFormatterVerra(resilienceCreditsPerHectare)}
            </Headline>
            <span className="mr-1">&nbsp;</span>

            {type === ResilienceCreditType.PEOPLE && (
              <span className="mr-1 text-lg pt-2"></span>
            )}
          </div>
        </div>
      </div>
      {!isValid && (
        <Text level={2} className="text-red-500 mb-3 text-center">
          Please enter a valid number
        </Text>
      )}
      <div className="w-full flex flex-row justify-between mb-4">
        <div className="w-full pl-2">
          <Text
            as="label"
            level={1}
            className="text-center flex items-center justify-center text-neutral-midfi-grey"
          >
            Total Asset Value:
          </Text>
          <Headline
            level={3}
            className="w-full text-center font-bold text-secondary-lightBlue flex flex-row items-center justify-center"
          >
            {type === ResilienceCreditType.PROPERTY && "$"}
            {kFormatterVerra(totalValue)}
            {type === ResilienceCreditType.PEOPLE && " People"}
          </Headline>
        </div>
      </div>
    </div>
  );
}

const sum = (acc: number, cur: number) => acc + cur;

const reducers = {
  sum: (values: number[]) => values.reduce(sum, 0),
};

const getStat = (
  metric: string,
  selectedFeatures: MapGeoJSONFeature[],
  distinct = false,
) => {
  const values = selectedFeatures
    .map((x) => x.properties?.[metric])
    .filter((value) => value !== undefined && value !== null && !isNaN(value));
  const uniqueValues = distinct ? [...new Set(values)] : values;
  return reducers.sum(uniqueValues);
};

const VERRA_LAYER_NAMES = {
  people: [
    LayerName.TESSELA_RPS_VERRA_PEOPLE_RESILIENCE_CREDITS,
    LayerName.TESSELA_BOUNDS_VERRA_PEOPLE_RESILIENCE_CREDITS,
  ],
  property: [
    LayerName.TESSELA_RPS_VERRA_PROPERTY_RESILIENCE_CREDITS,
    LayerName.TESSELA_BOUNDS_VERRA_PROPERTY_RESILIENCE_CREDITS,
  ],
};

function SelectedFeaturesVerraPanel({
  selectedFeatures,
  layersToggle,
  toggleLayer,
}: {
  selectedFeatures: MapGeoJSONFeature[];
  layersToggle: Record<string, boolean>;
  toggleLayer: (layer: LayerName) => void;
}) {
  const [hectares, setHectares] = useState<number>(1);
  // selectedCountries removed as it's not currently used

  // filteredPopBenData removed as it's not currently used

  const mangrovesLatest = useMemo(
    () => getStat(`Mang_Ha_${VERRA_REF_YEAR}`, selectedFeatures),
    [selectedFeatures],
  );

  const stockLatest = useMemo(
    () => getStat(`Ben_Stock_${VERRA_REF_YEAR}`, selectedFeatures),
    [selectedFeatures],
  );

  const popLatest = useMemo(
    () => getStat(`Ben_Pop_${VERRA_REF_YEAR}`, selectedFeatures),
    [selectedFeatures],
  );

  const peopleResilinceCredits = useMemo(
    () => (mangrovesLatest === 0 ? 0 : popLatest / mangrovesLatest),
    [mangrovesLatest, popLatest],
  );

  const propertyResilinceCredits = useMemo(
    () => (mangrovesLatest === 0 ? 0 : stockLatest / mangrovesLatest),
    [mangrovesLatest, stockLatest],
  );

  const mode: "people" | "property" = layersToggle[
    LayerName.TESSELA_RPS_VERRA_PEOPLE_RESILIENCE_CREDITS
  ]
    ? "people"
    : "property";

  const handleSwitchUpdate = () => {
    Object.entries(VERRA_LAYER_NAMES).forEach(([key, value]) => {
      toggleLayer(value[0]);
      toggleLayer(value[1]);
    });
  };

  return (
    <>
      <TemplateMetricContainer
        icon={layerGroups[LayerGroupName.BenefitsPerHectare].IconComponent}
        description="Resilience Assets are calculated based on the benefits to people and property per hectare of restored or conserved mangroves."
        title="Resilience Assets"
      >
        <div className="flex w-full justify-center">
          <MultiSwitch
            onChange={handleSwitchUpdate}
            selection={mode}
            theme="light"
          >
            <MultiSwitchOption title="Social" value="people" position="left" />
            <MultiSwitchOption
              title="Economic"
              value="property"
              position="right"
            />
          </MultiSwitch>
        </div>
        <div className="flex flex-row items-center flex-wrap w-full">
          {mode === "people" && (
            <>
              <Headline
                level={3}
                className="ml-2 mt-6 mb-3 w-full flex flex-row items-center text-neutral-midfi-grey"
              >
                Resilience Asset Calculator:
              </Headline>
              <ResilienceCreditCalculator
                hectares={hectares}
                setHectares={setHectares}
                resilienceCreditsPerHectare={peopleResilinceCredits}
                type={ResilienceCreditType.PEOPLE}
              />
              <Headline
                level={3}
                className="ml-2 mb-2 mt-6 w-full flex flex-row items-center text-neutral-midfi-grey"
              >
                Annual Benefits per Hectare:
              </Headline>
              <div className="px-14 w-full">
                <div className="flex w-full items-center justify-between">
                  <Text level={2} className="ml-6 text-neutral-midfi-grey">
                    Benefits (Social):
                  </Text>
                  <Text level={2} className="ml-2 text-neutral-midfi-grey">
                    {kFormatterVerra(popLatest)}
                  </Text>
                </div>

                <div className="flex w-full items-center justify-between">
                  <Text
                    level={2}
                    className="flex items-center text-neutral-midfi-grey"
                  >
                    <span className="mr-[10px]" style={{ fontSize: "1.2em" }}>
                      ÷{" "}
                    </span>
                    Hectares of Mangroves:
                  </Text>
                  <Text
                    level={2}
                    className="lining-nums text-neutral-midfi-grey"
                  >
                    {kFormatterVerra(mangrovesLatest)}
                  </Text>
                </div>

                <div className="my-1 mx-auto pr-4 self-center border-solid border-t border-primary-blue" />
                <div className="flex w-full items-center justify-between">
                  <Text level={2} className="ml-6 text-neutral-midfi-grey">
                    Benefits per Hectare:
                  </Text>
                  <Text
                    level={2}
                    className="ml-2 lining-nums font-bold text-neutral-midfi-grey"
                  >
                    {kFormatterVerra(peopleResilinceCredits)}
                  </Text>
                </div>
              </div>
            </>
          )}
          {mode === "property" && (
            <>
              <Headline
                level={3}
                className="ml-2 mt-6 mb-3 w-full flex flex-row items-center text-neutral-midfi-grey"
              >
                Resilience Asset Calculator:
              </Headline>
              <ResilienceCreditCalculator
                hectares={hectares}
                setHectares={setHectares}
                resilienceCreditsPerHectare={propertyResilinceCredits}
                type={ResilienceCreditType.PROPERTY}
              />
              <Headline
                level={3}
                className="ml-2 mb-2 mt-6 w-full text-neutral-midfi-grey"
              >
                Annual Benefits per Hectare:
              </Headline>
              <div className="px-14 w-full">
                <div className="flex w-full items-center justify-between">
                  <Text className="ml-6 text-neutral-midfi-grey" level={2}>
                    Benefits (Economic):
                  </Text>
                  <Text
                    level={2}
                    className="ml-2 lining-nums text-neutral-midfi-grey"
                  >
                    ${kFormatterVerra(stockLatest)}
                  </Text>
                </div>

                <div className="flex w-full items-center justify-between">
                  <Text
                    level={2}
                    className="flex items-center text-neutral-midfi-grey"
                  >
                    <span className="mr-[10px]" style={{ fontSize: "1.2em" }}>
                      ÷{" "}
                    </span>
                    Hectares of Mangroves:
                  </Text>
                  <Text
                    level={2}
                    className="lining-nums text-neutral-midfi-grey"
                  >
                    {kFormatterVerra(mangrovesLatest)}
                  </Text>
                </div>

                <div className="my-1 mx-auto pr-4 self-center border-solid border-t border-primary-blue" />
                <div className="flex w-full items-center justify-between">
                  <Text className="ml-6 text-neutral-midfi-grey" level={2}>
                    Benefits per Hectare:
                  </Text>
                  <Text
                    level={2}
                    className="ml-2 lining-nums font-bold text-neutral-midfi-grey"
                  >
                    ${kFormatterVerra(propertyResilinceCredits)}
                  </Text>
                </div>
              </div>
            </>
          )}
        </div>
      </TemplateMetricContainer>
      <div className="pt-6 pb-6 px-4 bottom-citation text-neutral-midfi-grey">
        Benefits based on Menéndez et al. (2020) and used in Verra Deemed
        Estimate method.
      </div>
    </>
  );
}

export default SelectedFeaturesVerraPanel;
